import React, { useState, useEffect, useMemo } from 'react';
import { PATIENT_PAYMENT_MODES, paymentNeedsProof } from '../constants/payments';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { doctorAPI, medicalServiceAPI, visitAPI } from '../services/api';
import Select from './Select';
import { PAYMENT_MODE_LABEL } from './TokenPrintModal';
import useDialog from '../hooks/useDialog';
import { useCashDrawer, isDrawerCancelled } from './cashDrawer/CashDrawerContext';

const rs = (n) => `₨${Number(n || 0).toLocaleString()}`;

/**
 * Correct a token that was issued with the wrong details.
 *
 * Only reachable before the doctor calls the patient in. Recorded vitals do not close the window:
 * they measure the patient, not the doctor, so they carry across a correction untouched. The
 * backend enforces the same rule and answers 409 VISIT_NOT_EDITABLE if the queue moved on while
 * this modal was open, which is surfaced inline rather than as a toast so the receptionist sees it
 * next to the form they were filling.
 *
 * Reassigning the doctor renumbers the token into that doctor's sequence, so the caller is told via
 * onSaved's `doctorChanged` flag and offers a reprint.
 */
const EditTokenModal = ({ visit, doctors = [], onClose, onSaved }) => {
  const originalDoctorId = visit?.doctor?.id || '';
  const originalServiceId = visit?.medicalService?.id || '';

  const [doctorId, setDoctorId] = useState(originalDoctorId);
  const [serviceId, setServiceId] = useState(originalServiceId);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [paymentMode, setPaymentMode] = useState(visit?.paymentMode || 'CASH');
  const [emergency, setEmergency] = useState(!!visit?.isEmergency);
  const [emergencyReason, setEmergencyReason] = useState(visit?.emergencyReason || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const editTokenDialogRef = useDialog(() => { if (!saving) onClose(); });
  const { withDrawer } = useCashDrawer();

  // Token Queue only loads the doctor list for non-doctor users (a doctor sees just their own
  // queue, so the filter is hidden). Reassignment still needs the full list, so fetch it here when
  // the caller had nothing to pass.
  const [doctorList, setDoctorList] = useState(doctors);
  useEffect(() => {
    if (doctors && doctors.length) { setDoctorList(doctors); return undefined; }
    let cancelled = false;
    doctorAPI.getAll()
      .then((d) => { if (!cancelled) setDoctorList(d || []); })
      .catch(() => { if (!cancelled) setDoctorList([]); });
    return () => { cancelled = true; };
  }, [doctors]);

  // Seed the discount from the stored Rs amount. If it doesn't land on one of the preset steps
  // (legacy data, or a discount entered before the presets existed) the exact percentage is added
  // as an extra option so opening the modal can't silently round someone's discount away.
  const originalPercent = useMemo(() => {
    const base = Number(visit?.originalFee || 0);
    const disc = Number(visit?.discountAmount || 0);
    if (base <= 0 || disc <= 0) return 0;
    return Math.round((disc / base) * 100);
  }, [visit]);

  useEffect(() => { setDiscountPercent(originalPercent); }, [originalPercent]);

  // Services are scoped per doctor (their own plus the general ones), so the list is refetched
  // whenever the doctor changes. A service belonging to the previous doctor may not exist for the
  // new one, so the selection is cleared unless it survives in the new list.
  useEffect(() => {
    let cancelled = false;
    if (!doctorId) { setServices([]); return undefined; }
    setLoadingServices(true);
    medicalServiceAPI.getForDoctor(doctorId)
      .then((data) => {
        if (cancelled) return;
        const list = data || [];
        setServices(list);
        setServiceId((current) => (list.some((s) => s.id === current) ? current : ''));
      })
      .catch(() => { if (!cancelled) setServices([]); })
      .finally(() => { if (!cancelled) setLoadingServices(false); });
    return () => { cancelled = true; };
  }, [doctorId]);

  const selectedService = services.find((s) => s.id === serviceId);
  const baseFee = Number(selectedService?.baseFee || 0);
  const discountAmount = Math.round((baseFee * discountPercent) / 100);
  const payable = Math.max(0, baseFee - discountAmount);
  const doctorChanged = doctorId !== originalDoctorId;

  // Split and Other are retired, but a token issued before that still carries one. Keeping the
  // current value in the list means the field isn't mysteriously blank when such a token is opened;
  // saving any other choice quietly migrates it off the retired mode.
  const paymentOptions = useMemo(() => {
    const offered = [
      ...PATIENT_PAYMENT_MODES,
    ];
    const current = visit?.paymentMode;
    if (current && current !== 'NONE' && !offered.some((o) => o.value === current)) {
      offered.push({ value: current, label: `${PAYMENT_MODE_LABEL[current] || current} (no longer offered)` });
    }
    return offered;
  }, [visit]);

  const percentOptions = useMemo(() => {
    const steps = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    const all = steps.includes(originalPercent) ? steps : [...steps, originalPercent].sort((a, b) => a - b);
    return all.map((p) => ({ value: p, label: p === 0 ? 'No discount' : `${p}%` }));
  }, [originalPercent]);

  // A token that consumed patient credit can't be edited: changing the fee would leave the credit
  // ledger inconsistent. The backend refuses it too, but surfacing it up front means nobody fills
  // the form only to be turned away on save.
  const creditApplied = Number(visit?.appliedCreditAmount || 0);
  const creditBlocked = creditApplied > 0;

  const canSave =
    doctorId && serviceId && !saving && !creditBlocked && (!emergency || emergencyReason.trim());

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // A changed cash fee, or a fee moved to or from cash, takes or hands back cash at the desk.
      const newMode = payable > 0 ? paymentMode : 'NONE';
      const oldMode = visit?.paymentMode || 'NONE';
      const oldFee = Number(visit?.finalFee || 0);
      const cashMoves = (oldMode === 'CASH' || newMode === 'CASH') && (oldMode !== newMode || oldFee !== payable);
      const updated = await withDrawer(() => visitAPI.update(visit.id, {
        doctorId,
        medicalServiceId: serviceId,
        discountAmount,
        // A zero-payable visit records NONE on the backend regardless of what's sent here.
        paymentMode: newMode,
        isEmergency: emergency,
        emergencyReason: emergency ? emergencyReason.trim() : null,
      }), { cash: cashMoves });
      onSaved(updated, { doctorChanged });
    } catch (e) {
      if (isDrawerCancelled(e)) {
        setSaving(false);
        return;
      }
      setError(e?.body?.message || e?.message || 'Could not update the token. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div ref={editTokenDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Edit Token</h3>
        <p className="text-sm text-gray-600 mb-4">
          <span className="font-medium">{visit.patient?.firstName} {visit.patient?.lastName}</span>
          {visit.patient?.mrn && <> · {visit.patient.mrn}</>}
        </p>

        {error && (
          <div className="rounded-xl px-4 py-3 mb-4 text-sm bg-red-50 border border-red-200 text-red-800">
            {error}
          </div>
        )}

        {creditBlocked && (
          <div className="rounded-xl px-4 py-3 mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-900">
            This token used {rs(creditApplied)} of patient credit, so its fee can't be edited.
            Cancel it and issue a new token instead, which returns the credit to the patient's balance.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Doctor <span className="text-red-500">*</span>
            </label>
            <Select
              value={doctorId}
              onChange={setDoctorId}
              placeholder="Select a doctor"
              options={doctorList.map((d) => ({
                value: d.id,
                label: d.fullName + (d.specialization ? ' - ' + d.specialization : ''),
              }))}
              buttonClassName="px-3 py-3 rounded-lg"
            />
            {doctorChanged && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                Token numbers run per doctor, so moving this patient issues a new number in the new
                doctor's queue. The slip they are holding will be out of date, so print a new one.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Medical Service <span className="text-red-500">*</span>
            </label>
            <Select
              value={serviceId}
              disabled={!doctorId || loadingServices}
              placeholder={
                loadingServices ? 'Loading services...'
                  : !doctorId ? 'Select a doctor first'
                  : services.length === 0 ? 'No services available for this doctor'
                  : 'Select a service'
              }
              onChange={setServiceId}
              options={services.map((s) => ({
                value: s.id,
                label: `${s.name} - ₨${s.baseFee}${s.doctor ? ' (doctor-specific)' : ''}`,
              }))}
              buttonClassName="px-3 py-3 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Discount (%)</label>
            <Select
              value={discountPercent}
              onChange={(v) => setDiscountPercent(parseInt(v, 10) || 0)}
              options={percentOptions}
              buttonClassName="px-3 py-3 rounded-lg"
            />
          </div>

          {payable > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mode of Payment <span className="text-red-500">*</span>
              </label>
              <Select
                value={paymentMode}
                onChange={setPaymentMode}
                options={paymentOptions}
                buttonClassName="px-3 py-3 rounded-lg"
              />
            </div>
          )}

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={emergency}
              onChange={(e) => setEmergency(e.target.checked)}
              className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <span className="text-sm font-medium text-gray-700">Mark as Emergency (Priority Queue)</span>
          </label>

          {emergency && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emergency Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={emergencyReason}
                onChange={(e) => setEmergencyReason(e.target.value)}
                rows="2"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Why is this an emergency?"
              />
            </div>
          )}

          {selectedService && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Service fee</span>
                <span className="font-medium text-gray-900">{rs(baseFee)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between mb-1 text-emerald-700">
                  <span>Discount ({discountPercent}%)</span>
                  <span className="font-medium">-{rs(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 mt-2 border-t border-gray-200">
                <span className="font-semibold text-gray-900">Payable</span>
                <span className="font-bold text-gray-900">{rs(payable)}</span>
              </div>
              {Number(visit.finalFee || 0) !== payable && (
                <p className="flex items-start gap-1.5 text-amber-700 mt-3">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Was {rs(visit.finalFee)}, now {rs(payable)}. Settle the difference with the patient.</span>
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTokenModal;
